import { useEffect, useRef, useState } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { LandTourCategory, LandTourPriceDate } from "../../../interfaces/land-tour.interface";
import { sortedTiers } from "./components/ChooseYourTour";

export interface PriceQuote {
  price: number;
  saveAmount: number;
  beforeSaveAmount: number;
  source: "server" | "local";
}

interface Args {
  landTourId: number | undefined;
  category: LandTourCategory | undefined;
  date: string | undefined;
  priceDate: LandTourPriceDate | undefined;
  quantity: number;
}

const DEBOUNCE_MS = 300;

// Mirrors LandTours::PriceQuote (app/services/land_tours/price_quote.rb, iceholidays#4453):
//
//   before_save_amount = first_tier_price + category.save_amount
//   save_amount        = before_save_amount - selected_price
//
// so the sidebar's "Starting From" and save-amount strikethrough work today, computed from
// data already in the show payload, and switch over silently once
// GET /b2c2b/api/v1/land_tours/:id/calculate_price (PR #4532) ships - see the implementation
// plan's ยง1.1. Every render tries the server first; on any failure (including "doesn't exist
// yet", a 404) it falls back to the identical local formula, and stops retrying a route that
// isn't there so pax clicks don't each fire a doomed request for the rest of the session.
//
// Expect the two to disagree by under RM1: the jbuilder ceils each tier's adult price
// (`value.dig("price").to_d.ceil`) while PriceQuote works in raw decimals. The server value
// wins whenever it's available, which is exactly why this discrepancy is tolerated rather
// than "fixed" to match - matching the ceil here would just make the local path wrong in a
// different way once the server starts responding.
export function usePriceQuote({ landTourId, category, date, priceDate, quantity }: Args): {
  quote: PriceQuote | undefined;
  loading: boolean;
} {
  const [quote, setQuote] = useState<PriceQuote | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const endpointUnavailable = useRef(false);

  useEffect(() => {
    setQuote(localQuote(priceDate, category, quantity));

    if (!landTourId || !category || !date || endpointUnavailable.current) return undefined;

    let active = true;
    const timer = setTimeout(() => {
      setLoading(true);
      axiosInstance
        .get(`/b2c2b/api/v1/land_tours/${landTourId}/calculate_price`, {
          params: { category_id: category.id, depart_date: date, quantity: Math.max(quantity, 1) },
        })
        .then((response) => {
          if (!active) return;
          const data = response.data || {};
          setQuote({
            price: Number(data.price) || 0,
            saveAmount: Number(data.save_amount) || 0,
            beforeSaveAmount: Number(data.before_save_amount) || 0,
            source: "server",
          });
        })
        .catch((error) => {
          // Local quote from above already covers this render - just remember not to keep
          // asking a route that doesn't exist (404) or isn't wired for this verb (405).
          const status = error?.response?.status;
          if (status === 404 || status === 405) endpointUnavailable.current = true;
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [landTourId, category, date, quantity]);

  return { quote, loading };
}

function localQuote(
  priceDate: LandTourPriceDate | undefined,
  category: LandTourCategory | undefined,
  quantity: number
): PriceQuote | undefined {
  if (!priceDate || !category) return undefined;
  const tiers = sortedTiers(priceDate);
  if (!tiers.length) return undefined;

  const pax = Math.max(quantity, 1);
  const selectedTier =
    tiers.find((tier) => tier.quantity.includes(pax)) ||
    (pax < (tiers[0].quantity[0] ?? 0) ? tiers[0] : tiers[tiers.length - 1]);

  const beforeSaveAmount = tiers[0].adult + (category.saveAmount || 0);
  const price = selectedTier.adult;

  return {
    price,
    saveAmount: beforeSaveAmount - price,
    beforeSaveAmount,
    source: "local",
  };
}
